// Reminiscence101 — the science behind remembr, surfaced inline so
// visitors don't skip the critical evidence by clicking through to the
// full research page. Includes a TL;DR card with the five most important
// evidence bullets (Cochrane, NICE, Alzheimer's Association, key effect
// sizes, no-evidence-of-harm) directly on the landing.
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { FmnWatermark } from "../FmnWatermark";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export function Reminiscence101() {
    const { t } = useTranslation();
    const tldr = t("rt101.tldr_items", { returnObjects: true });
    const tldrList = Array.isArray(tldr) ? tldr : [];

    return (
        <section
            className="relative py-28 px-6 bg-[#FAF7F2] overflow-hidden"
            id="reminiscence"
            data-testid="reminiscence-101"
        >
            <FmnWatermark position="right" size={740} opacity={0.05} rotate={-10} />
            <div className="max-w-6xl mx-auto">
                <motion.div {...FADE} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-5">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-6">
                            {t("rt101.eyebrow")}
                        </p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight">
                            {t("rt101.headline_part1")}{" "}
                            <em className="italic text-[#4A5A95]">
                                {t("rt101.headline_emphasis")}
                            </em>
                        </h2>
                    </div>

                    <div className="lg:col-span-7 space-y-5 text-[#5C5A59] text-lg leading-relaxed font-light max-w-prose">
                        <p>{t("rt101.p1")}</p>
                        <p>{t("rt101.p2")}</p>
                        <p>{t("rt101.p3")}</p>
                    </div>
                </motion.div>

                {/* TL;DR card — inline so nobody skips the evidence */}
                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.15 }}
                    className="mt-14 rounded-[2rem] bg-white border border-[#E5E1D8] shadow-rem-card p-8 lg:p-12"
                    data-testid="rt101-tldr"
                >
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-6">
                        {t("rt101.tldr_eyebrow")}
                    </p>
                    <ul className="space-y-4 text-[#2D2B2A] leading-relaxed font-light">
                        {tldrList.map((item, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-3"
                                data-testid={`rt101-tldr-item-${i + 1}`}
                            >
                                <CheckCircle size={20} weight="duotone" className="shrink-0 mt-1 text-[#6B7DB8]" />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-8 pt-6 border-t border-[#E5E1D8] flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#8A8786]">
                        <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#5C5A59]">
                            {t("rt101.sources_label")}
                        </span>
                        <span data-testid="rt101-source-cochrane">
                            <strong className="text-[#2D2B2A] font-medium">Cochrane (Woods et al., 2018)</strong>
                            {" "}· 22 trials · 1,972 participants
                        </span>
                        <span data-testid="rt101-source-nice">
                            <strong className="text-[#2D2B2A] font-medium">NICE NG97 (UK)</strong>
                            {" "}· mild–moderate dementia
                        </span>
                        <span data-testid="rt101-source-alz">
                            <strong className="text-[#2D2B2A] font-medium">Alzheimer&rsquo;s Association</strong>
                            {" "}· core care practice
                        </span>
                    </div>

                    <div className="mt-7 pt-6 border-t border-[#E5E1D8] flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <p className="text-[#5C5A59] italic max-w-md">
                            For clinicians, investors and researchers — the full evidence pack with citations and effect-size tables.
                        </p>
                        <Link
                            to="/research"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4A5A95] hover:bg-[#2D2B4A] text-white font-medium text-sm transition-colors shadow-rem-soft"
                            data-testid="rt101-research-cta"
                        >
                            Open the full dementia research pack
                            <ArrowRight weight="bold" size={14} />
                        </Link>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
